// Stable cart-wide selection: exclude cart products and duplicates, preserve merchant order.
export function selectAddonIds(candidates, cartIds, existingIds, limit) {
  const seen = new Set([...cartIds, ...existingIds].map(String));
  const chosen = [];
  for (const id of candidates) {
    const key = String(id);
    if (!/^\d+$/.test(key) || seen.has(key)) continue;
    if (chosen.length + existingIds.length >= limit) break;
    seen.add(key);
    chosen.push(key);
  }
  return chosen;
}

if (typeof customElements !== 'undefined' && !customElements.get('mc-cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.onSubmit = this.add.bind(this);
      this.addEventListener('submit', this.onSubmit);
      this.controller = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      this.controller.abort();
    }

    async load() {
      const limit = Math.max(1, Math.min(6, Number(this.dataset.limit) || 4));
      const list = this.querySelector('[data-mc-addon-list]');
      const ids = [...new Set((this.dataset.seedIds || '').split(',').filter(id => /^\d+$/.test(id)))].slice(0, 8);
      if (list.children.length >= limit) return;
      const requests = ids.map(async id => {
        const url = new URL(this.dataset.recommendationsUrl, location.origin);
        url.search = new URLSearchParams({product_id:id, intent:'complementary', limit:'10', section_id:'mc-cart-recommendations'});
        const response = await fetch(url, {signal:this.controller.signal, credentials:'same-origin'});
        if (!response.ok) throw new Error('Recommendations unavailable');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      });
      const results = await Promise.allSettled(requests);
      if (!this.isConnected || this.controller.signal.aborted) return;
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const cards = [...result.value.querySelectorAll('[data-mc-addon]')];
        const existingIds = [...list.children].map(el => el.dataset.productId);
        const selected = new Set(selectAddonIds(cards.map(el => el.dataset.productId), (this.dataset.cartProductIds || '').split(','), existingIds, limit));
        for (const card of cards) {
          if (selected.delete(card.dataset.productId)) list.append(card);
        }
      }
      this.hidden = !list.children.length;
    }

    async add(event) {
      const form = event.target.closest('[data-mc-addon-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button').forEach(button => { button.disabled = true; });
      const error = this.querySelector('[data-mc-addon-error]');
      error.hidden = true;
      const service = window.cartService;
      const title = form.dataset.productTitle;
      try {
        if (!service) throw new Error('Please refresh the page and try again.');
        const response = await fetch(form.action + '.js', {
          method:'POST', credentials:'same-origin',
          headers:{'Content-Type':'application/json', 'Accept':'application/json'},
          body:JSON.stringify({items:[{id:Number(new FormData(form).get('id')), quantity:1}], sections:service.getCartSectionsToBeRerendered(), sections_url:location.pathname})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added. Please try again.');
        service.rerenderCartSections(result);
        // Reopening an already-open native dialog queues a close event. That
        // event removes its freshly installed dismissal handlers.
        if (!document.querySelector('#minicart')?.open) service.openSlideout();
        await service.getCart();
        const status = document.querySelector('[data-mc-cart-upsell-status]');
        if (status) status.textContent = `${title} added to your bag.`;
        const heading = document.querySelector('#minicart [data-mc-addon-heading]') || document.querySelector('#minicart h2');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({preventScroll:true});
      } catch (cause) {
        if (this.isConnected) {
          error.textContent = cause instanceof TypeError ? 'We could not confirm the update. Check your bag before trying again.' : cause.message;
          error.hidden = false;
        }
      } finally {
        if (this.isConnected) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.querySelectorAll('button').forEach(button => { button.disabled = false; });
        }
      }
    }
  }
  customElements.define('mc-cart-upsell', CartUpsell);
}
